import type { PrepareEditorCompatibleFileData } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/files/[file_uuid]/editor-compatible'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import * as Sentry from '@sentry/nextjs'

interface IPrepareEditorCompatibleFileParams {
    organizationId: string
    brandSlug: string
    fileUuid: string
}

interface IPrepareEditorCompatiblePollingOptions {
    pollIntervalMs?: number
    timeoutMs?: number
}

const POLL_INTERVAL_MS = 2_000
const TIMEOUT_MS = 10 * 60_000

export class PrepareEditorCompatibleFileMutation {
    private static path({ organizationId, brandSlug, fileUuid }: IPrepareEditorCompatibleFileParams) {
        return `/organization/${organizationId}/brands/${brandSlug}/files/${fileUuid}/editor-compatible`
    }

    static async call(
        api: IApi,
        params: IPrepareEditorCompatibleFileParams,
        polling: IPrepareEditorCompatiblePollingOptions = {}
    ) {
        const startedAt = Date.now()
        const path = this.path(params)
        const pollIntervalMs = polling.pollIntervalMs ?? POLL_INTERVAL_MS
        const timeoutMs = polling.timeoutMs ?? TIMEOUT_MS
        try {
            let result = await api.post<PrepareEditorCompatibleFileData, Record<string, never>>(path, {})
            if (result.data.status === 'READY') return result.data

            const deadline = Date.now() + timeoutMs
            while (Date.now() < deadline) {
                await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
                result = await api.get<PrepareEditorCompatibleFileData>(path)
                if (result.data.status === 'READY') return result.data
            }

            throw new Error('Editor-compatible video conversion timed out')
        } catch (error) {
            Sentry.captureException(error, {
                tags: { operation: 'prepare-editor-compatible-video' },
                extra: { fileUuid: params.fileUuid, elapsedMs: Date.now() - startedAt },
            })
            throw error
        }
    }
}

export const usePrepareEditorCompatibleFileMutation = () => {
    const { api, apiParams } = useApiToolbox()
    return useApiMutation({
        mutationFn: (fileUuid: string) =>
            PrepareEditorCompatibleFileMutation.call(api, {
                ...apiParams,
                fileUuid,
            }),
    })
}
